import React from "react";
import { ArrowUpRight } from "lucide-react";
import ResponsiveImg from "./ResponsiveImg";

const MAIN_SITE = "https://viewluxwindows.com";

export const PRODUCTS = [
  {
    code: "VLX · 80",
    title: "Casement Window",
    desc: "Full sash opening for enhanced ventilation, modular configuration, and large glass area for expansive views and abundant daylight.",
    img: "/viewlux/products/casement.jpg",
    href: `${MAIN_SITE}/casement/`,
  },
  {
    code: "VLX · 80",
    title: "Fix / Picture Window",
    desc: "Pure, uninterrupted views with maximum glass and minimal frame for a seamless indoor-outdoor connection.",
    img: "/viewlux/products/fix-picture.jpg",
    href: `${MAIN_SITE}/fixed/`,
  },
  {
    code: "VLX · 500",
    title: "Sliding Window",
    desc: "Slim aluminum profiles and large glass panels maximize daylight while maintaining a clean horizontal aesthetic.",
    img: "/viewlux/products/sliding-window.jpg",
    href: `${MAIN_SITE}/sliding-window/`,
  },
  {
    code: "VLX · 80",
    title: "Awning Window",
    desc: "Top-hinged design for ventilation with rain protection — ideal for higher wall placements requiring privacy and airflow.",
    img: "/viewlux/products/awning.jpg",
    href: `${MAIN_SITE}/awning/`,
  },
  {
    code: "VLX · 500",
    title: "Double Hung / Hung",
    desc: "Timeless functionality with clean contemporary design — slim aluminum profiles and insulated glass options for year-round indoor comfort.",
    img: "/viewlux/products/double-hung.jpg",
    href: `${MAIN_SITE}/hung-double-hung/`,
  },
  {
    code: "VLX · Corner",
    title: "Corner / Specialty Shape",
    desc: "Eliminate the visual barrier of a corner mullion. Crafted for unique architectural expressions — angled, arched, and custom.",
    img: "/viewlux/products/corner-specialty.jpg",
    href: `${MAIN_SITE}/grills/`,
  },
  {
    code: "VLX · 100",
    title: "Sliding Doors",
    desc: "Clean lines, insulated glass options for year-round indoor comfort, and superior build quality — minimal sightlines for seamless indoor-outdoor flow.",
    img: "/viewlux/products/sliding-door.jpg",
    href: `${MAIN_SITE}/panoramic-sliding/`,
  },
  {
    code: "VLX · 150",
    title: "Heavy-Duty Sliding",
    desc: "Engineered for modern architecture with ultra-thin panels and reinforced aluminum structure for exceptional structural performance.",
    img: "/viewlux/products/vlx150.jpg",
    href: `${MAIN_SITE}/heavy-duty-sliding/`,
  },
  {
    code: "VLX · 138",
    title: "Panoramic Door",
    desc: "Recessed bottom track installs within the finished floor — a flush threshold and a seamless transition between spaces.",
    img: "/viewlux/products/vlx138-panoramic.jpg",
    href: `${MAIN_SITE}/panoramic-sliding/`,
  },
  {
    code: "VLX · 100",
    title: "Single Swing Door",
    desc: "Refined aesthetics with reliable performance for modern entryways — narrow aluminum frames and minimalist hardware.",
    img: "/viewlux/products/swing.jpg",
    href: `${MAIN_SITE}/swing/`,
  },
  {
    code: "VLX · 100",
    title: "Double Swing / French",
    desc: "Timeless elegance with modern performance — a wide, inviting opening with slim thermally broken aluminum profiles.",
    img: "/viewlux/products/double-swing.jpg",
    href: `${MAIN_SITE}/double-swing-door/`,
  },
  {
    code: "VLX · 100",
    title: "Pivot Door",
    desc: "A bold architectural statement — grand scale and precise engineering for a striking entrance experience.",
    img: "/viewlux/products/pivot.jpg",
    href: `${MAIN_SITE}/pivot-door/`,
  },
  {
    code: "VLX · 100",
    title: "Bi-Folding Door",
    desc: "Multiple folding configurations stack to one or both sides — slim aluminum profiles maximize glass area for full openness.",
    img: "/viewlux/products/bifold.jpg",
    href: `${MAIN_SITE}/folding-bifold-door/`,
  },
  {
    code: "VLX · Arches",
    title: "Arched Windows & Doors",
    desc: "Where timeless arches meet modern performance — refined focal points balancing classic and contemporary spaces.",
    img: "/viewlux/products/arches.jpg",
    href: `${MAIN_SITE}/arched-swing-door/`,
  },
  {
    code: "VLX · MT150",
    title: "Heavy-Duty Sliding",
    desc: "Engineered for smooth, effortless operation. Wide-opening patio system delivering expansive views, structural strength, and consistent energy efficiency when closed.",
    img: "/viewlux/products/heavy-duty-mt150.jpg",
    href: `${MAIN_SITE}/heavy-duty-sliding/`,
  },
  {
    code: "VLX · Glass",
    title: "Glass Railing",
    desc: "Frameless glass railings for balconies, terraces, and stairs — uninterrupted views, refined architectural lines, and engineered structural safety.",
    img: "/viewlux/products/glass-railing.jpg",
    href: `${MAIN_SITE}/glass-railing/`,
  },
];

export default function ProductCollection({ onRequestQuote }) {
  return (
    <section
      id="collection"
      data-testid="product-collection"
      className="relative bg-cream"
    >
      <div className="container-vlx py-24 md:py-36">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-16 md:mb-20">
          <div className="md:col-span-7">
            <span className="eyebrow">The Collection</span>
            <div className="mt-4 rule-gold" />
            <h2 className="heading-lg mt-8 text-[36px] md:text-[64px] text-[#0f2420]">
              A complete aluminum system for{" "}
              <em className="italic font-light text-[#7a5b2f]">
                modern openings.
              </em>
            </h2>
          </div>
          <div className="md:col-span-5 md:pb-4">
            <p className="text-neutral-700 text-[15px] leading-[1.95] font-nunito">
              From fixed and operable windows to large sliding and multi-panel
              door systems, ViewLux ensures consistent quality across all
              lines.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#c99658]/25">
          {PRODUCTS.map((p, i) => (
            <article
              key={p.title + i}
              data-testid={`product-card-${i}`}
              className="group relative bg-cream overflow-hidden transition-colors duration-500 hover:bg-white"
            >
              <div className="relative overflow-hidden bg-cream aspect-[4/3]">
                <ResponsiveImg
                  src={p.img}
                  alt={`${p.code} ${p.title}`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                  className="absolute inset-0 w-full h-full object-contain p-2 transition-transform duration-[2000ms] ease-out group-hover:scale-[1.02]"
                />
                <span className="absolute top-5 left-5 text-[10px] tracking-[0.4em] uppercase font-raleway text-[#e2b880] bg-[#0f2420]/70 backdrop-blur px-3 py-1.5">
                  {p.code}
                </span>
              </div>
              <div className="p-7 md:p-8 border-t border-[#c99658]/15">
                <h3 className="heading-lg text-[24px] md:text-[28px] text-[#0f2420] leading-[1.1]">
                  {p.title}
                </h3>
                <p className="mt-4 text-neutral-700 text-[14.5px] leading-[1.8] font-nunito min-h-[66px]">
                  {p.desc}
                </p>
                <div className="mt-6 flex items-center gap-6">
                  <a
                    data-testid={`product-learn-${i}`}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Learn more about ${p.title}`}
                    className="link-arrow"
                  >
                    Learn More
                    <span className="sr-only"> about {p.title}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                  <button
                    data-testid={`product-cta-${i}`}
                    onClick={onRequestQuote}
                    aria-label={`Request quote for ${p.title}`}
                    className="text-[10.5px] tracking-[0.32em] uppercase font-raleway text-[#0f2420] hover:text-[#c99658] transition-colors"
                  >
                    Request Quote
                  </button>
                </div>
              </div>
            </article>
          ))}
          {/* Filler tiles so the bronze grid-gap doesn't show next to the last card on incomplete rows */}
          {(() => {
            const cols = 3;
            const rem = PRODUCTS.length % cols;
            const fillers = rem === 0 ? 0 : cols - rem;
            return Array.from({ length: fillers }).map((_, idx) => (
              <div
                key={`filler-${idx}`}
                aria-hidden="true"
                className="hidden lg:block bg-cream"
              />
            ));
          })()}
          {/* Tablet (2 cols) filler */}
          {(() => {
            const cols = 2;
            const rem = PRODUCTS.length % cols;
            const fillers = rem === 0 ? 0 : cols - rem;
            return Array.from({ length: fillers }).map((_, idx) => (
              <div
                key={`filler-sm-${idx}`}
                aria-hidden="true"
                className="hidden sm:block lg:hidden bg-cream"
              />
            ));
          })()}
        </div>
      </div>
    </section>
  );
}
